import fetch from '@ixigo/fetch';
import { type ApiErrorResponse, executeApiCall } from '@/api';
import type { SearchFormData } from '../store';

export interface TripTypeResponse {
	data: {
		type: 'AIRPORT_DROPOFF' | 'AIRPORT_PICKUP';
		msg: string;
		minDurationInMinutes: number;
	};
}

type TripTypeSearchData = Required<Omit<SearchFormData, 'dateTime'>>;

interface CacheEntry {
	data: TripTypeResponse | ApiErrorResponse<unknown>;
	timestamp: number;
	searchFormDataKey: string;
}

const tripTypeCache = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 5 * 60 * 1000;

function buildTripTypeRequest(searchFormData: TripTypeSearchData) {
	return {
		pickup: {
			latitude: searchFormData.pickup.lat,
			longitude: searchFormData.pickup.lng,
			placeId: searchFormData.pickup.placeId,
		},
		drop: {
			latitude: searchFormData.drop.lat,
			longitude: searchFormData.drop.lng,
			placeId: searchFormData.drop.placeId,
		},
	};
}

function generateCacheKey(searchFormData: TripTypeSearchData): string {
	return JSON.stringify(searchFormData);
}

function isCacheValid(entry: CacheEntry): boolean {
	return Date.now() - entry.timestamp < CACHE_TTL_MS;
}

function cleanExpiredCache(): void {
	const now = Date.now();
	for (const [key, entry] of tripTypeCache.entries()) {
		if (now - entry.timestamp >= CACHE_TTL_MS) {
			tripTypeCache.delete(key);
		}
	}
}

export const fetchTripType = async (
	searchFormData: TripTypeSearchData,
	abortController?: AbortSignal,
): Promise<TripTypeResponse | ApiErrorResponse<unknown>> => {
	const cacheKey = generateCacheKey(searchFormData);

	const cachedEntry = tripTypeCache.get(cacheKey);
	if (cachedEntry && isCacheValid(cachedEntry)) {
		return cachedEntry.data;
	}

	cleanExpiredCache();

	const request = buildTripTypeRequest(searchFormData);
	const response = await executeApiCall(
		() =>
			fetch
				.post<TripTypeResponse>('cabs/api/v1/trip-type', {
					body: JSON.stringify(request),
					headers: {
						'Content-Type': 'application/json',
					},
					signal: abortController,
				})
				.json(),
		'fetchTripType',
	);

	tripTypeCache.set(cacheKey, {
		data: response,
		timestamp: Date.now(),
		searchFormDataKey: cacheKey,
	});

	return response;
};
